import React from 'react';
import chroma from 'chroma-js';
import styled, { css, useTheme } from 'styled-components/macro';
import { IconMinus, IconCheck } from '../../icons';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { SPACINGS } from '../../utils';

type CheckboxWrapperProps = {
  error?: string;
  disabled?: boolean;
  accentColor: string;
  backgroundColor: string;
  errorColor: string;
};

export const CheckboxWrapper = styled.div<CheckboxWrapperProps>`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  color: ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.black)};
  font-family: ${(p) => p.theme.fonts.literata};
  font-size: ${sizesMap['base'].size};
  line-height: ${textLineHeight};

  input {
    height: 18px;
    width: 18px;
    position: relative;
    background-color: ${(p) => p.backgroundColor};
    border: none;
    margin: 0;
    border-radius: 0;
    outline: none;
  }

  input:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    background-color: ${(p) =>
      p.error ? chroma(p.errorColor).brighten(3).hex() : p.backgroundColor};
    border: 1px solid
      ${(p) => (p.error ? p.errorColor : p.theme.colors.secondary80w)};
    border-radius: 0;
    cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};
  }

  label {
    position: relative;
    margin-bottom: 0;
    cursor: pointer;
    margin-left: ${SPACINGS[2]};
  }

  input:not(:disabled) {
    :hover :before {
      outline: unset;
      border: 1px solid ${(p) => chroma(p.accentColor).darken(0.5).hex()};
    }

    :focus :before {
      outline: unset;
      border: 1px solid ${(p) => chroma(p.accentColor).darken(0.5).hex()};
    }

    :active :before {
      outline: unset;
      border: 1px solid ${(p) => chroma(p.accentColor).darken(0.5).hex()};
    }
  }

  input ~ .checkmark {
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    display: none;
    height: 18px;
    width: 18px;
    z-index: 2;
    justify-content: center;
    align-items: center;
  }

  input:checked ~ .checkmark {
    display: flex;
  }

  .mark {
    width: 12px;
    height: 12px;
    background-color: ${(p) => chroma(p.accentColor).darken(0.5).hex()};
  }

  input:checked:disabled ~ .checkmark {
    svg {
      g {
        stroke: ${(p) => p.theme.colors.black80w};
      }
    }
  }

  input:checked:disabled {
    cursor: not-allowed;
    background-color: ${(p) => p.theme.colors.secondary90w};
    border: 1px solid ${(p) => p.theme.colors.secondary80w};
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.3;
    `}
`;

type CheckboxProps = {
  id?: string;
  name: string;
  label?: string;
  defaultChecked?: boolean;
  value?: string | number | string[];
  error?: string;
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  testid?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  labelStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  accentColor?: string;
  backgroundColor?: string;
  errorColor?: string;
};
function Checkbox(props: CheckboxProps) {
  const theme = useTheme();
  const {
    id,
    name,
    label,
    value,
    checked,
    defaultChecked,
    indeterminate,
    onChange = () => {},
    onClick = () => {},
    readOnly,
    disabled,
    error,
    testid,
    labelStyle,
    style,
    children,
    accentColor = theme.colors.primary,
    backgroundColor = theme.colors.white,
    errorColor = theme.colors.danger,
  } = props;

  return (
    <CheckboxWrapper
      disabled={disabled}
      role="checkbox"
      aria-checked={checked}
      error={error}
      style={style}
      onClick={onClick}
      accentColor={accentColor}
      backgroundColor={backgroundColor}
      errorColor={errorColor}
    >
      <input
        data-testid={testid}
        type="checkbox"
        id={id || name}
        name={name}
        checked={checked}
        // @ts-ignore
        indeterminate={indeterminate ? 1 : 0}
        defaultChecked={defaultChecked}
        value={value}
        disabled={disabled}
        onChange={onChange}
        readOnly={readOnly}
      />
      <div className="checkmark">
        {indeterminate ? (
          <IconMinus stroke={chroma(accentColor).darken(0.5).hex()} />
        ) : (
          // <div className="mark" />
          <IconCheck stroke={chroma(accentColor).darken(0.5).hex()} />
        )}
      </div>
      {(label || children) && (
        <label style={labelStyle} htmlFor={id || name}>
          {label || children}
        </label>
      )}
    </CheckboxWrapper>
  );
}

export default React.memo(Checkbox);
